import { Box } from '@mui/material'
import Typography from '@mui/material/Typography'
import { styled } from '@mui/styles'
import { IconAws } from '_app/icons/IconAws'
import { BellRing } from 'lucide-react'
import { SnackbarContent, useSnackbar } from 'notistack'
import { forwardRef, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import CloseButton from './CloseButton'
import { Link } from './Link'

export const MonitoringNotification = forwardRef(({ id, ...props }, ref) => {
  const { closeSnackbar } = useSnackbar()

  const onClick = useCallback(() => {
    props.onActionClick()
    closeSnackbar(id)
  }, [id])
  return (
    <SnackbarContent ref={ref}>
      <Wrapper>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: '10px',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '15px',
            }}
          >
            <Box
              sx={{
                border: (theme) => `1px solid ${theme.palette.border.main}`,
                borderRadius: '4px',
                minWidth: '35px',
                minHeight: '35px',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <IconAws size={25} />
            </Box>
            <Box>
              <Typography variant="textSecondary" color="text.secondary">
                There are no instrumented resources {props?.isInService && 'in this service'}
              </Typography>
              <Link
                onClick={onClick}
                variant="link"
                size="small"
                sx={{
                  fontSize: (theme) => theme.typography.textSecondary.fontSize,
                  textDecoration: 'underline',
                }}
              >
                <Typography variant="textSecondary">
                  Click here to instrument {props.compatibleFunctions} resources
                </Typography>
              </Link>
            </Box>
          </Box>
          <CloseButton onClick={props.onCancel} size="small" />
        </Box>
      </Wrapper>
    </SnackbarContent>
  )
})
export const AddIntegrationNotification = forwardRef(({ id, ...props }, ref) => {
  const { closeSnackbar } = useSnackbar()
  const navigate = useNavigate()
  const handleDismiss = useCallback(() => {
    closeSnackbar(id)
  }, [id, closeSnackbar])
  const onClick = useCallback(() => {
    navigate({
      search: `?createIntegration=1`,
    })
    handleDismiss()
  }, [id])
  return (
    <SnackbarContent ref={ref}>
      <Wrapper>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: '10px',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '15px',
            }}
          >
            <Box
              sx={{
                border: (theme) => `1px solid ${theme.palette.border.main}`,
                borderRadius: '4px',
                minWidth: '35px',
                minHeight: '35px',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <IconAws size={25} />
            </Box>
            <Box>
              <Typography variant="textSecondary" color="text.secondary">
                There are no AWS integrations found
              </Typography>
              <Link
                onClick={onClick}
                variant="link"
                size="small"
                sx={{
                  fontSize: (theme) => theme.typography.textSecondary.fontSize,
                  textDecoration: 'underline',
                }}
              >
                <Typography variant="textSecondary">
                  Click here to connect your AWS account
                </Typography>
              </Link>
            </Box>
          </Box>
          <CloseButton onClick={props.onCancel} size="small" />
        </Box>
      </Wrapper>
    </SnackbarContent>
  )
})

export const PriceChangeNotification = forwardRef(({ id, ...props }, ref) => {
  const { closeSnackbar } = useSnackbar()
  const onClose = useCallback(() => {
    props.onClose()
    closeSnackbar(id)
  }, [id])

  return (
    <SnackbarContent ref={ref}>
      <Wrapper>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '5px',
            maxWidth: 300,
            backgroundColor: (theme) => theme.palette.background.default,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
            }}
          >
            <BellRing size={15} />
            <Typography variant="textPrimary">Pricing Update - February 2024</Typography>
            <CloseButton onClick={onClose} sx={{ marginLeft: 'auto' }} size="small" />
          </Box>
          <Typography variant="textSecondary" color="text.secondary">
            As of February 2024 our pricing is changing to use Credits covering both Serverless
            Framework v.4 and Dashboard Service Instances.{' '}
            <Link to="https://serverless.com/pricing" underline="always">
              Please visit our pricing page for more info.
            </Link>
          </Typography>
        </Box>
      </Wrapper>
    </SnackbarContent>
  )
})

const Wrapper = styled(Box)(({ theme }) => ({
  padding: '15px 15px 15px 20px',
  backgroundColor: theme.palette.background.default,
  border: `1px solid ${theme.palette.border.main}`,
  borderRadius: '4px',
}))
